import React from "react";
import { renderToString } from "react-dom/server";
import Banner from "../../../b2b-common/Banner";
import { getObjectiveType } from "../objectives/QuantitativeObjectiveEditForm";
import Status from "../Status";
import Trend from "../Trend";
import { getIsoDate } from "../../../b2b-common/helpers";

export const getStatusColor = (status) => {
    switch (status) {
        case 'GREEN':
            return '#008000'; // Green
        case 'YELLOW':
            return '#FFFF00'; // Yellow
        case 'RED':
            return '#FF0000'; // Red
        default:
            return '#000000'; // Default to black if status is unknown
    }
};

export const calculatePaddedDates = (startDate, endDate, paddingPercentage) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDiff = end - start;
    const paddingTime = timeDiff * paddingPercentage;

    const paddedStart = new Date(start.getTime() - paddingTime);
    const paddedEnd = new Date(end.getTime() + paddingTime);

    return {
        paddedStart: paddedStart.toISOString(),
        paddedEnd: paddedEnd.toISOString()
    };
};

export const calculateMinMaxDatesFromDatesArray = (dates = []) => {
    if (dates.length === 0) {
        return {
            minDate: new Date(),
            maxDate: new Date()
        };
    }

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    return {
        minDate,
        maxDate
    };
}

export const calculateMinMaxValuesFromValuesArray = (values = [], paddingPercentage) => {
    if (values.length === 0) {
        return {
            minValue: 0,
            maxValue: 0
        };
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    let adjustedMinValue, adjustedMaxValue;

    if (maxValue === minValue) {
        // Calculate padding based on the start value if min and max are the same
        adjustedMinValue = minValue - (minValue * paddingPercentage);
        adjustedMaxValue = maxValue + (maxValue * paddingPercentage);
    } else {
        adjustedMinValue = minValue - ((maxValue - minValue) * paddingPercentage);
        adjustedMaxValue = maxValue + ((maxValue - minValue) * paddingPercentage);
    }

    const flooredMinValue = Math.floor(adjustedMinValue);
    const finalMinValue = flooredMinValue > 0 ? flooredMinValue : 0;

    const ceiledMaxValue = Math.ceil(adjustedMaxValue);

    return {
        minValue: finalMinValue,
        maxValue: ceiledMaxValue
    };
}

export const calculateMinMaxValues = (startValue, endValue, sampleValues, paddingPercentage) => {
    const allValues = [startValue, endValue, ...sampleValues];
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    let adjustedMinValue, adjustedMaxValue;

    if (maxValue === minValue) {
        // Calculate padding based on the start value if min and max are the same
        adjustedMinValue = minValue - (minValue * paddingPercentage);
        adjustedMaxValue = maxValue + (maxValue * paddingPercentage);
    } else {
        adjustedMinValue = minValue - ((maxValue - minValue) * paddingPercentage);
        adjustedMaxValue = maxValue + ((maxValue - minValue) * paddingPercentage);
    }

    const flooredMinValue = Math.floor(adjustedMinValue);
    const finalMinValue = flooredMinValue > 0 ? flooredMinValue : 0;

    const ceiledMaxValue = Math.ceil(adjustedMaxValue);

    return {
        minValue: finalMinValue,
        maxValue: ceiledMaxValue
    };
};

export const quantValueWithUnit = (value, indicatorDTO) => {
    return `${value} ${indicatorDTO.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}`;
}

export const calculateObjectiveSegmentValues = (objectiveDTO, startDate, endDate) => {
    const objectiveStartDate = getIsoDate(objectiveDTO.startDate);
    const objectiveEndDate = getIsoDate(objectiveDTO.endDate);
    const objectiveStartValue = objectiveDTO.startQuantitativeValue;
    const objectiveEndValue = objectiveDTO.endQuantitativeValue;

    const objectiveSegment = {
        objectiveType: getObjectiveType(objectiveDTO),
        startDate: getIsoDate(startDate),
        endDate: getIsoDate(endDate),
        startValue: 0,
        endValue: 0
    };

    if (objectiveSegment.objectiveType === 'Regular') {
        const objectiveSegmentNumDays = (objectiveEndDate - objectiveStartDate) / (1000 * 60 * 60 * 24);
        const objectiveSegmentValues = objectiveEndValue - objectiveStartValue;
        objectiveSegment.startValue = Math.round(objectiveStartValue +
            ((objectiveSegmentValues / objectiveSegmentNumDays) * ((getIsoDate(startDate) - objectiveStartDate) / (1000 * 60 * 60 * 24))));
        objectiveSegment.endValue = Math.round(objectiveStartValue +
            ((objectiveSegmentValues / objectiveSegmentNumDays) * ((getIsoDate(endDate) - objectiveStartDate) / (1000 * 60 * 60 * 24))));
    } else if ((objectiveSegment.objectiveType === 'Threshold_Above') || (objectiveSegment.objectiveType === 'Threshold_Below')) {
        objectiveSegment.startValue = objectiveStartValue;
        objectiveSegment.endValue = objectiveStartValue;
    } else if (objectiveSegment.objectiveType === 'Range') {
        objectiveSegment.startValue = objectiveStartValue;
        objectiveSegment.endValue = objectiveEndValue;
    }
    return objectiveSegment;
}

// TOOL TIP FORMATTERS

export function renderObjectiveTooltip(title, dateValue, valueValue) {
    return (
        <div className="quantitative-objective-tooltip">
            <Banner message={title} underline={false} headingLevel='h5' className="quantitative-objective-tooltip-banner" />
            {renderTooltipData(dateValue, valueValue)}
        </div>
    );
}

export function renderQuantitativeRegularObjectiveStartTooltip(startDate, startValue, indicatorDTO) {
    return renderObjectiveTooltip('Objective Start', startDate, quantValueWithUnit(startValue, indicatorDTO));
}

export function renderQuantitativeRegularObjectiveEndTooltip(endDate, endValue, indicatorDTO) {
    return renderObjectiveTooltip('Objective End', endDate, quantValueWithUnit(endValue, indicatorDTO));
}

export function renderQuantitativeThresholdObjectiveTooltip(date, value, indicatorDTO) {
    return renderObjectiveTooltip('Objective Threshold', date, quantValueWithUnit(value, indicatorDTO));
}

export function renderQuantitativeLowerRangeObjectiveTooltip(date, value, indicatorDTO) {
    return renderObjectiveTooltip('Objective Lower Range', date, quantValueWithUnit(value, indicatorDTO));
}

export function renderQuantitativeUpperRangeObjectiveTooltip(date, value, indicatorDTO) {
    return renderObjectiveTooltip('Objective Upper Range', date, quantValueWithUnit(value, indicatorDTO));
}

export function regularQuantitativeObjectiveFormatter(params, objective, indicatorDTO) {
    const pointIndex = params.dataIndex;
    const startDate = new Date(objective.startDate).toLocaleDateString();
    const startValue = objective.startQuantitativeValue;
    const endDate = new Date(objective.endDate).toLocaleDateString();
    const endValue = objective.endQuantitativeValue;

    if (pointIndex === 0) {
        return renderToString(renderQuantitativeRegularObjectiveStartTooltip(startDate, startValue, indicatorDTO));
    } else if (pointIndex === 1) {
        return renderToString(renderQuantitativeRegularObjectiveEndTooltip(endDate, endValue, indicatorDTO));
    }
}

export function thresholdQuantitativeObjectiveFormatter(params, objective, indicatorDTO) {
    const pointIndex = params.dataIndex;
    const date = new Date(objective.startDate).toLocaleDateString();
    const value = objective.startQuantitativeValue;
    if ((pointIndex === 0) || (pointIndex === 1)) {
        return renderToString(renderQuantitativeThresholdObjectiveTooltip(date, value, indicatorDTO));
    }
}

export function lowerRangeQuantitativeObjectiveFormatter(params, objective, indicatorDTO) {
    const pointIndex = params.dataIndex;
    const date = new Date(objective.startDate).toLocaleDateString();
    const value = objective.startQuantitativeValue;
    if ((pointIndex === 0) || (pointIndex === 1)) {
        return renderToString(renderQuantitativeLowerRangeObjectiveTooltip(date, value, indicatorDTO));
    }
}

export function higherRangeQuantitativeObjectiveFormatter(params, objective, indicatorDTO) {
    const pointIndex = params.dataIndex;
    const date = new Date(objective.startDate).toLocaleDateString();
    const value = objective.endQuantitativeValue;
    if ((pointIndex === 0) || (pointIndex === 1)) {
        return renderToString(renderQuantitativeUpperRangeObjectiveTooltip(date, value, indicatorDTO));
    }
}

export function renderTooltipData(date, value) {
    return (
        <div className="quantitative-tooltip-data">
            <div className="quantitative-tooltip-data-date">
                <div className="quantitative-tooltip-data-date-label">Date</div>
                <div className="quantitative-tooltip-data-date-value">{date}</div>
            </div>
            <div className="quantitative-tooltip-data-value">
                <div className="quantitative-tooltip-data-value-label">Value</div>
                <div className="quantitative-tooltip-data-value-value">{value}</div>
            </div>
        </div>
    );
}

export function renderSampleStatusTrend(status, trend) {
    return (
        <div className="quantitative-sample-status-trend">
            <Status
                status={status}
                className={'area-indicator-card-status'}
                withTooltip={false}
            />
            <Trend
                trend={trend}
                className={'area-indicator-card-trend'}
                withTooltip={false}
            />
        </div>
    );
}

export function renderSampleTooltip(date, value, status, trend, indicatorDTO) {
    return (
        <div className="quantitative-sample-tooltip">
            <Banner message='Sample' underline={false} headingLevel='h5' className="quantitative-sample-tooltip-banner" />
            {renderTooltipData(date, quantValueWithUnit(value, indicatorDTO))}
            {renderSampleStatusTrend(status, trend)}
        </div>
    );
}

export function getDimensionValueName(dimensionOrder, dimensionalType) {
    const dimensionValue = dimensionalType.dimensionValues.find(dv => dv.dimensionValueOrder === dimensionOrder);
    return dimensionValue ? dimensionValue.dimensionValueName : '';
}